/* eslint-disable @typescript-eslint/no-unused-expressions */

import type {CaseReducer, PayloadAction} from '@reduxjs/toolkit'

import type {PipelineId} from '../../../../../types'
import type {JobFile} from '../../types'

import type {PipelineDraftState} from './EditPipelinePage.slices.types'
import {
  deleteIfEmpty,
  ensurePipelineDraftEntry,
  getJob,
  getPipelineDraftEntry,
} from './EditPipelinePage.slices.utils'

export const setFilesPublication: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    jobId: string
    index: number
    file: JobFile
    branch: string
  }>
> = (state, action) => {
  const {branch, pipelineId, jobId, index, file} = action.payload
  const job = getJob(state, pipelineId, jobId, branch)

  if (job != null) {
    job['files-publication'] ??= []
    job['files-publication'][index] = file
  }
}

export const addFilesPublication: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; jobId: string; branch: string}>
> = (state, action) => {
  const {branch, pipelineId, jobId} = action.payload
  const job = getJob(state, pipelineId, jobId, branch)

  if (job != null) {
    job['files-publication'] ??= []
    job['files-publication'].push({
      path: '',
      'share-with-jobs': true,
      'publish-artifact': false,
    })
    const jobFilesPublicationLength = job['files-publication']?.length
    const jobFilesPublicationLengthIndex = jobFilesPublicationLength - 1
    const indexKey = `${jobId}_${jobFilesPublicationLengthIndex}`

    const pipeline = getPipelineDraftEntry(state, pipelineId, branch)!
    pipeline.added ??= {}
    pipeline.added!.filesPublication ??= {}
    pipeline.added!.filesPublication![indexKey] = true
  }
}

export const deleteFilesPublication: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    jobId: string
    index: number
    branch: string
  }>
> = (state, action) => {
  const {branch, pipelineId, jobId, index} = action.payload
  const job = getJob(state, pipelineId, jobId, branch)
  const jobFilesPublication = job?.['files-publication']
  const indexKey = `${jobId}_${index}`
  const addedFilesPublication = getPipelineDraftEntry(state, pipelineId, branch)?.added
    ?.filesPublication

  if (jobFilesPublication && addedFilesPublication?.[indexKey]) {
    job['files-publication'] = jobFilesPublication.filter((_, fileIndex) => fileIndex !== index)

    const pipeline = getPipelineDraftEntry(state, pipelineId, branch)!
    pipeline.added!.filesPublication = job['files-publication'].reduce<Record<string, boolean>>(
      (acc, _, fileIndex) => {
        acc[`${jobId}_${fileIndex}`] = true

        return acc
      },
      {},
    )

    deleteIfEmpty(pipeline.added, 'filesPublication') && deleteIfEmpty(pipeline, 'added')
  } else {
    const pipeline = ensurePipelineDraftEntry(state, pipelineId, branch)
    pipeline.deleted ??= {}
    pipeline.deleted!.filesPublication ??= []
    pipeline.deleted!.filesPublication!.push(indexKey)
  }
}

export const restoreFilesPublication: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; jobId: string; index: number; branch: string}>
> = (state, action) => {
  const {branch, pipelineId, jobId, index} = action.payload
  const {deleted} = getPipelineDraftEntry(state, pipelineId, branch) ?? {}
  const indexKey = `${jobId}_${index}`

  if (deleted?.filesPublication?.includes(indexKey)) {
    deleted.filesPublication = deleted?.filesPublication.filter(item => item !== indexKey)
    deleteIfEmpty(deleted, 'filesPublication') &&
      deleteIfEmpty(getPipelineDraftEntry(state, pipelineId, branch), 'deleted')
  }
}

export const clearFilesPublication: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; jobId: string; branch: string}>
> = (state, action) => {
  const {branch, pipelineId, jobId} = action.payload
  const {added} = getPipelineDraftEntry(state, pipelineId, branch) ?? {}
  const job = getJob(state, pipelineId, jobId, branch)

  if (added?.filesPublication != null && job != null) {
    delete added.filesPublication
    deleteIfEmpty(getPipelineDraftEntry(state, pipelineId, branch), 'added')
  }
}

export const multiplyDeleteFilesPublication: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    jobId: string
    indexes: number[]
    branch: string
  }>
> = (state, action) => {
  const {branch, pipelineId, jobId, indexes} = action.payload
  const job = getJob(state, pipelineId, jobId, branch)
  const jobFilesPublication = job?.['files-publication']

  if (jobFilesPublication) {
    job['files-publication'] = jobFilesPublication.filter(
      (_, fileIndex) => !indexes.includes(fileIndex),
    )

    const pipeline = getPipelineDraftEntry(state, pipelineId, branch)!
    if (pipeline.added?.filesPublication != null) {
      pipeline.added.filesPublication ??= job['files-publication'].reduce<Record<string, boolean>>(
        (acc, _, fileIndex) => {
          acc[`${jobId}_${fileIndex}`] = true

          return acc
        },
        {},
      )
    }

    deleteIfEmpty(pipeline.added, 'filesPublication') && deleteIfEmpty(pipeline, 'added')
  }
}

export const applyAddedFilesPublication: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; branch: string}>
> = (state, action) => {
  const {pipelineId, branch} = action.payload
  const {added} = getPipelineDraftEntry(state, pipelineId, branch) ?? {}

  if (added?.filesPublication != null) {
    delete added.filesPublication
    deleteIfEmpty(getPipelineDraftEntry(state, pipelineId, branch), 'added')
  }
}
